import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type ChangeEvent, type FormEvent, type KeyboardEvent } from "react";
import { NavLink } from "react-router-dom";
import { ChevronRight, Paperclip, Pencil, Plus, X } from "lucide-react";
import {
  closeConsultationPro,
  createConsultationPro,
  createMedicalRecord,
  createVaccinationPro,
  getPatientByNip,
  getPatientMedicalFilePro,
  getPatientPrescriptionsPro,
} from "../../../api/professionalPatient";
import { PatientCreationWizard } from "./AddPatient";
import PatientAccessOtpModal from "../../../components/PatientAccessOtpModal";
import { filterOrdonnanceRowsForConsultation } from "../../../features/pro/ordonnancePharmacyHelpers";
import SendOrdonnanceToPharmacyDialog, {
  buildOrdonnanceSendOptionsFromProRows,
} from "../../../features/pro/SendOrdonnanceToPharmacyDialog";
import { uploadFilesToBucket } from "../../../api/bucket";
import {
  addDocumentToConsultation,
  ALLOWED_MIME_TYPES,
  type TypeDocument,
} from "../../../api/professionalPatient";
import FileAttachmentLink from "../../../components/ui/FileAttachment";
import { useProfessionalAuth } from "../../../store/authProfessionalStore";
import { showToast } from "../../../components/ui/toastHotSonner";
import { getMesEtablissements, type MonEtablissementItem } from "../../../api/professional";
import { getMaladiesEpidemiques } from "../../../api/publicBenin";
import {
  CONSULTATION_EXIT_STATES,
  CONSULTATION_STEPS,
  buildConsultationComplement,
  createEmptyNewConsultationForm,
  type ConsultationExitState,
  truncateObservations,
  type NewConsultationFormState,
} from "../../../features/pro/consultationComplement";

type VerifiedPatient = {
  nip: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  adresse: string;
  profession: string;
  dateNaissance: string;
  createur: string | null;
};
type ConsultationStatus = "ouverte" | "fermee" | "annulee";

function mapPatientSummary(data: unknown, fallbackNip: string): VerifiedPatient {
  const pr = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const rawBirth = String(pr.dateNaissance ?? pr.date_naissance ?? "");
  return {
    nip: String(pr.nip ?? fallbackNip),
    nom: String(pr.nom ?? ""),
    prenom: String(pr.prenom ?? ""),
    email: String(pr.email ?? ""),
    telephone: String(pr.telephone ?? ""),
    adresse: String(pr.adresse ?? ""),
    profession: String(pr.profession ?? ""),
    dateNaissance: rawBirth ? (rawBirth.includes("T") ? rawBirth : `${rawBirth}T00:00:00.000Z`) : "",
    createur: typeof pr.createur === "string" ? pr.createur : null,
  };
}

/**
 * `existe: false` = compte patient déjà créé mais aucun dossier médical rempli — c'est le seul cas
 * où le professionnel peut soumettre `POST /dossiers-medicaux` (le backend interdit toute
 * modification d'`informationsMedicales` une fois le dossier créé, y compris pour ce rôle).
 */
type DossierState = "idle" | "loading" | "needs-otp" | "empty" | "filled" | "error";

export default function QuickDiagnostic() {
  const { professional } = useProfessionalAuth();
  const proNip = professional?.nip?.trim();

  const [nip, setNip] = useState("");
  const nipPartRefs = useRef<Array<HTMLInputElement | null>>([]);
  const nipValid = useMemo(() => /^\d{10}$/.test(nip), [nip]);
  const nipParts = useMemo(
    () => [nip.slice(0, 2), nip.slice(2, 4), nip.slice(4, 6), nip.slice(6, 8), nip.slice(8, 10)],
    [nip],
  );

  const updateNipPart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const parts = [...nipParts];
    parts[index] = digits;
    setNip(parts.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) {
      requestAnimationFrame(() => nipPartRefs.current[index + 1]?.focus());
    }
  };

  const handleNipPartKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = nipPartRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try {
          prev.setSelectionRange(prev.value.length, prev.value.length);
        } catch {
          /* ignore */
        }
      });
    }
  };

  const handleNipPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    setNip(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => nipPartRefs.current[nextIndex]?.focus());
  };

  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showCreatePatient, setShowCreatePatient] = useState(false);
  const [verifiedPatient, setVerifiedPatient] = useState<VerifiedPatient | null>(null);
  const [monEtablissements, setMonEtablissements] = useState<MonEtablissementItem[]>([]);
  const [monEtablissementsLoading, setMonEtablissementsLoading] = useState(false);
  const [selectedEtablissementId, setSelectedEtablissementId] = useState("");

  const [dossierState, setDossierState] = useState<DossierState>("idle");
  const [dossierError, setDossierError] = useState<string | null>(null);
  const [existingDossier, setExistingDossier] = useState<{ numeroDossier: string; statut: string } | null>(null);

  const [dossierTaille, setDossierTaille] = useState("");
  const [dossierPoids, setDossierPoids] = useState("");
  const [dossierGs, setDossierGs] = useState("");
  const [dossierElectro, setDossierElectro] = useState("");
  const [dossierAllergies, setDossierAllergies] = useState("");
  /** Voir AddPatient.tsx `VaccinRow` : lot/siteInjection sans quoi le backend retombe sur "N/A". */
  type VaccinRow = { nom: string; lot: string; siteInjection: string };
  const [dossierVaccins, setDossierVaccins] = useState<VaccinRow[]>([
    { nom: "Polio", lot: "", siteInjection: "" },
    { nom: "Fievre jaune", lot: "", siteInjection: "" },
    { nom: "COVID-19", lot: "", siteInjection: "" },
  ]);
  const [dossierSubmitting, setDossierSubmitting] = useState(false);
  const [dossierSubmitError, setDossierSubmitError] = useState<string | null>(null);

  const [consultationStep, setConsultationStep] = useState(0);
  const [newConsultation, setNewConsultation] = useState<NewConsultationFormState>(() =>
    createEmptyNewConsultationForm(),
  );
  const [etatSortie, setEtatSortie] = useState<ConsultationExitState>("en_traitement");
  const [maladiesReferentiel, setMaladiesReferentiel] = useState<string[]>([]);
  const [loadingMaladies, setLoadingMaladies] = useState(true);
  const [diseaseAutreMode, setDiseaseAutreMode] = useState(false);
  const [consultationId, setConsultationId] = useState<string | null>(null);
  const [consultationStatus, setConsultationStatus] = useState<ConsultationStatus | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  type PendingDoc = { bucketUrl: string; fileName: string; fileSize: number; mimeType: string; type: TypeDocument };
  const [analysisFiles, setAnalysisFiles] = useState<File[]>([]);
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);
  const [sentDocs, setSentDocs] = useState<PendingDoc[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadFileError, setUploadFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pharmacyDialogOpen, setPharmacyDialogOpen] = useState(false);
  const [pharmacySendOptions, setPharmacySendOptions] = useState<{ id: string; label: string }[]>([]);

  const tryOpenPharmacySend = useCallback(async (patientNip: string, consultationId: string) => {
    const loadOptionsOnce = async () => {
      const res = await getPatientPrescriptionsPro(patientNip);
      if (!res.ok) {
        setSubmitError(res.message);
        return [] as { id: string; label: string }[];
      }
      const matched = filterOrdonnanceRowsForConsultation(res.data, consultationId);
      return buildOrdonnanceSendOptionsFromProRows(matched);
    };
    const retryDelays = [0, 500, 1000, 1500, 2500, 3500];
    let options: { id: string; label: string }[] = [];
    for (const d of retryDelays) {
      if (d) await new Promise((r) => setTimeout(r, d));
      options = await loadOptionsOnce();
      if (options.length > 0) break;
    }
    if (options.length === 0) {
      setSubmitError(
        "Aucune ordonnance exploitable pour cette consultation (réseau ou dossier sans enregistrement SQL). Réessayez ou ouvrez la fiche patient.",
      );
      return;
    }
    setPharmacySendOptions(options);
    setPharmacyDialogOpen(true);
  }, []);

  const handleUploadAnalysisFiles = async () => {
    if (analysisFiles.length === 0) return;

    const invalidFiles = analysisFiles.filter(
      (f) => !(ALLOWED_MIME_TYPES as readonly string[]).includes(f.type)
    );
    if (invalidFiles.length > 0) {
      setUploadFileError(
        `Type non autorisé : ${invalidFiles.map((f) => f.name).join(", ")}. Formats acceptés : PDF, images (PNG/JPG), Word, Excel.`
      );
      return;
    }

    setUploadingFiles(true);
    setUploadFileError(null);
    const folder = consultationId
      ? `consultations-pieces-jointes-${consultationId}`
      : "consultations-pieces-jointes";
    const res = await uploadFilesToBucket(analysisFiles, folder);
    setUploadingFiles(false);
    if (!res.ok) { setUploadFileError(res.message); return; }

    const newDocs: PendingDoc[] = analysisFiles.map((f, i) => ({
      bucketUrl: res.urls[i],
      fileName: f.name,
      fileSize: f.size,
      mimeType: f.type,
      type: "analyse" as TypeDocument,
    }));

    if (consultationId) {
      // Consultation déjà ouverte → POST immédiat au backend
      const sent: PendingDoc[] = [];
      for (const doc of newDocs) {
        const r = await addDocumentToConsultation(consultationId, doc);
        if (r.ok) sent.push(doc);
      }
      setSentDocs((prev) => [...prev, ...sent]);
      if (sent.length < newDocs.length) {
        setUploadFileError(`${newDocs.length - sent.length} fichier(s) non enregistré(s) côté backend.`);
      }
    } else {
      // Pas encore de consultation → on garde les docs en attente
      setPendingDocs((prev) => [...prev, ...newDocs]);
    }

    setAnalysisFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const loadDossier = async (patientNip: string) => {
    setDossierState("loading");
    setDossierError(null);
    const res = await getPatientMedicalFilePro(patientNip);
    if (!res.ok) {
      if (res.errorCode === "ACCES_DOSSIER_REQUIS") {
        setDossierState("needs-otp");
        return;
      }
      setDossierState("error");
      setDossierError(res.message);
      return;
    }
    const data = res.data && typeof res.data === "object" ? (res.data as Record<string, unknown>) : {};
    if (data.existe === false) {
      setDossierState("empty");
      return;
    }
    setDossierState("filled");
    setExistingDossier({
      numeroDossier: String(data.numeroDossier ?? ""),
      statut: String(data.statut ?? ""),
    });
  };

  useEffect(() => {
    if (!proNip) return;
    setMonEtablissementsLoading(true);
    void (async () => {
      const res = await getMesEtablissements(proNip);
      setMonEtablissementsLoading(false);
      if (res.ok) setMonEtablissements(res.data);
    })();
  }, [proNip]);

  useEffect(() => {
    setLoadingMaladies(true);
    void (async () => {
      const res = await getMaladiesEpidemiques();
      setLoadingMaladies(false);
      if (res.ok) setMaladiesReferentiel(res.data);
    })();
  }, []);

  const verifyPatientNip = async (nipValue: string) => {
    setVerifyError(null);
    setSubmitError(null);
    setNotFound(false);
    setShowCreatePatient(false);
    setVerifiedPatient(null);
    setSelectedEtablissementId("");
    setDossierState("idle");
    setDossierError(null);
    setExistingDossier(null);
    if (!/^\d{10}$/.test(nipValue)) {
      setVerifyError("Le NIP patient doit comporter exactement 10 chiffres.");
      return;
    }
    setVerifyLoading(true);
    const res = await getPatientByNip(nipValue.trim());
    setVerifyLoading(false);
    if (!res.ok) {
      if (res.status === 404) {
        setNotFound(true);
        return;
      }
      setVerifyError(res.message);
      return;
    }
    const patient = mapPatientSummary(res.data, nipValue.trim());
    setVerifiedPatient(patient);
    void loadDossier(patient.nip);
  };

  const handleVerify = () => verifyPatientNip(nip);

  const handlePatientCreated = (patientNip: string) => {
    setShowCreatePatient(false);
    setNip(patientNip);
    void verifyPatientNip(patientNip);
  };

  const handleCreateDossier = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!verifiedPatient || !proNip || dossierSubmitting) return;
    setDossierSubmitting(true);
    setDossierSubmitError(null);
    const poidsN = dossierPoids ? Number(dossierPoids) : undefined;
    const tailleN = dossierTaille ? Number(dossierTaille) : undefined;
    const allergiesList = dossierAllergies.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    const dossierPayload: Record<string, unknown> = {
      patientNip: verifiedPatient.nip,
      informationsPersonnelles: {
        nom: verifiedPatient.nom,
        prenom: verifiedPatient.prenom,
        dateNaissance: verifiedPatient.dateNaissance,
        adresse: verifiedPatient.adresse,
        email: verifiedPatient.email,
        telephone: verifiedPatient.telephone,
        profession: verifiedPatient.profession || undefined,
      },
      informationsMedicales: {
        poids: poidsN,
        taille: tailleN,
        groupeSanguin: dossierGs || undefined,
        electrophorese: dossierElectro || undefined,
        allergies: allergiesList,
        intolerances: [] as string[],
        vaccinations: [] as unknown[],
        antecedents: [] as string[],
      },
      createur: proNip,
    };
    const res = await createMedicalRecord(dossierPayload);
    if (!res.ok) {
      setDossierSubmitting(false);
      if (res.errorCode === "ACCES_DOSSIER_REQUIS") {
        setDossierState("needs-otp");
        return;
      }
      setDossierSubmitError(res.message);
      return;
    }
    for (const v of dossierVaccins) {
      const vRes = await createVaccinationPro({
        patientNip: verifiedPatient.nip,
        vaccin: v.nom,
        lot: v.lot.trim() || undefined,
        siteInjection: v.siteInjection.trim() || undefined,
        dateVaccination: new Date().toISOString(),
        professionnelNip: proNip,
        createur: proNip,
      });
      if (!vRes.ok) {
        setDossierSubmitError((prev) =>
          prev ? `${prev} Vaccination "${v.nom}" non enregistrée : ${vRes.message}.` : `Vaccination "${v.nom}" non enregistrée : ${vRes.message}.`
        );
      }
    }
    setDossierSubmitting(false);
    setDossierState("filled");
    setExistingDossier({ numeroDossier: "", statut: "actif" });
    showToast("Dossier médical créé.", "success");
  };

  const handleSubmitConsultation = async () => {
    if (!verifiedPatient || !proNip) {
      setSubmitError(!proNip ? "NIP professionnel manquant." : "Patient non vérifié.");
      return;
    }
    if (consultationStatus === "fermee") {
      setSubmitError("Cette consultation est déjà clôturée (lecture seule).");
      return;
    }
    const isOpening = !consultationId;
    if (isOpening && !selectedEtablissementId) {
      setSubmitError("Sélectionnez l'établissement (ou votre cabinet) sous lequel créer cette consultation.");
      return;
    }
    setSubmitError(null);
    setSubmitLoading(true);
    const complement = buildConsultationComplement(newConsultation) as Record<string, unknown> | undefined;
    const obs = truncateObservations(newConsultation.observations);
    const openComplement =
      complement && Array.isArray(complement.analyses) && complement.analyses.length
        ? { analyses: complement.analyses }
        : undefined;
    const closeComplement: Record<string, unknown> = {};
    if (complement?.resultats && typeof complement.resultats === "object") {
      closeComplement.resultats = complement.resultats;
    }
    if (Array.isArray(complement?.ordonnances) && complement.ordonnances.length > 0) {
      closeComplement.ordonnances = complement.ordonnances;
    }
    const promptPharmacyAfterClose =
      !isOpening &&
      !!consultationId &&
      Array.isArray(complement?.ordonnances) &&
      complement.ordonnances.length > 0;
    const consultationIdForPharmacy = consultationId;
    const res = isOpening
      ? await createConsultationPro({
          patientNip: verifiedPatient.nip,
          professionnelNip: proNip,
          etablissementId: selectedEtablissementId,
          date: new Date().toISOString(),
          typeConsultation: "consultation_generale",
          motif: "Consultation initiale",
          observations: obs || undefined,
          createur: proNip,
          ...(openComplement ? { complement: openComplement } : {}),
        })
      : await closeConsultationPro(consultationId, {
          professionnelNip: proNip,
          etatSortie,
          ...(Object.keys(closeComplement).length > 0 ? { complement: closeComplement } : {}),
        });
    setSubmitLoading(false);
    if (!res.ok) {
      if (res.errorCode === "ETABLISSEMENT_LIAISON_REQUISE") {
        setSubmitError("Vous devez avoir une liaison active avec cet établissement pour y créer une consultation.");
        return;
      }
      if (res.status === 409) {
        setSubmitError("Cette consultation est déjà fermée ou annulée.");
        return;
      }
      if (res.status === 400) {
        setSubmitError("Données invalides pour cette étape (vérifiez les champs requis).");
        return;
      }
      if (res.status === 403) {
        setSubmitError("Droits insuffisants pour cette action.");
        return;
      }
      setSubmitError(res.message);
      return;
    }
    if (isOpening) {
      const data = res.data && typeof res.data === "object" ? (res.data as Record<string, unknown>) : {};
      const openIdRaw =
        data.id ??
        data.consultationId ??
        (data.data && typeof data.data === "object" ? (data.data as Record<string, unknown>).id : undefined);
      const openId = typeof openIdRaw === "string" ? openIdRaw.trim() : "";
      if (!openId) {
        setSubmitError("Consultation ouverte, mais identifiant introuvable dans la réponse serveur.");
        return;
      }
      setConsultationId(openId);
      setConsultationStatus("ouverte");
      // POST les docs en attente maintenant qu'on a l'ID
      if (pendingDocs.length > 0) {
        const sent: PendingDoc[] = [];
        for (const doc of pendingDocs) {
          const r = await addDocumentToConsultation(openId, doc);
          if (r.ok) sent.push(doc);
        }
        setSentDocs((prev) => [...prev, ...sent]);
        setPendingDocs([]);
      }
      setConsultationStep(1);
      showToast("Consultation ouverte. Vous pouvez maintenant la clôturer.", "success");
      return;
    }
    setConsultationStatus("fermee");
    showToast("Consultation clôturée.", "success");
    setConsultationStep(CONSULTATION_STEPS.length - 1);
    if (promptPharmacyAfterClose && consultationIdForPharmacy && verifiedPatient) {
      void tryOpenPharmacySend(verifiedPatient.nip, consultationIdForPharmacy);
    }
  };

  const patientDetailHref = verifiedPatient
    ? `/pro/dashboard/patients/${encodeURIComponent(verifiedPatient.nip)}`
    : "#";

  return (
    <div>
      <div className="text-white">
        <h1 className="text-3xl md:text-4xl font-bold">Consultation Rapide</h1>
        <p className="mt-2 max-w-2xl text-sm md:text-base text-white/90">
          Vérifiez que le patient possède un dossier (NIP à 10 chiffres), puis enregistrez une consultation sans
          passer par la création de patient.
        </p>
      </div>

      {(verifyError || submitError) && (
        <div className="mt-4 rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {verifyError ?? submitError}
        </div>
      )}

      <div
        className="mt-6 rounded-[10px] p-6 text-[#0B1F16]"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
          backdropFilter: "blur(6px)",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <div className="font-semibold text-lg">Étape 1 — Vérifier le NIP patient</div>
        <p className="mt-1 text-sm text-[#0B1F16]/75">Saisissez l’ANIP en 5 groupes de 2 chiffres.</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {nipParts.map((part, index) => (
            <div key={`nip-q-${index}`} className="flex items-center gap-2">
              <input
                ref={(el) => {
                  nipPartRefs.current[index] = el;
                }}
                inputMode="numeric"
                autoComplete="off"
                maxLength={2}
                value={part}
                onChange={(e) => updateNipPart(index, e.target.value)}
                onKeyDown={(e) => handleNipPartKeyDown(index, e)}
                onPaste={handleNipPaste}
                aria-invalid={nip.length > 0 && !nipValid}
                className="h-12 w-14 text-center rounded-[10px] border bg-white px-1 text-[#0B1F16] outline-none focus:ring-2 focus:ring-[#00553E]/30"
                style={{ borderColor: nipValid || nip === "" ? "#E5E7EB" : "#F87171" }}
              />
            </div>
          ))}
          <button
            type="button"
            disabled={verifyLoading || !nipValid}
            onClick={() => void handleVerify()}
            className="ml-2 h-12 px-5 rounded-[6px] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "#00553E", boxShadow: "0 2px 0 rgba(0,0,0,0.1)" }}
          >
            {verifyLoading ? "Vérification…" : "Vérifier"}
          </button>
        </div>

        {notFound && !showCreatePatient && (
          <div className="mt-4 rounded-[8px] border border-amber-200/90 bg-amber-50/95 px-4 py-3 text-sm text-amber-950 flex flex-wrap items-center justify-between gap-3">
            <span>Aucun patient trouvé avec le NIP {nip}. Ce patient n'a peut-être pas encore de compte.</span>
            <button
              type="button"
              onClick={() => setShowCreatePatient(true)}
              className="h-9 px-4 rounded-[8px] font-semibold text-white shrink-0"
              style={{ background: "#00553E" }}
            >
              Créer ce patient
            </button>
          </div>
        )}

        {verifiedPatient && (
          <div className="mt-5 rounded-[8px] border border-[#CDE5DB] bg-[#E8F4EF]/80 px-4 py-3 text-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-[#00553E]">Patient identifié</div>
                <div className="mt-1 text-[#0B1F16]">
                  <span className="text-[#0B1F16]/60">NIP : </span>
                  <span className="font-semibold">{verifiedPatient.nip}</span>
                </div>
              </div>
              <NavLink
                to={patientDetailHref}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-[8px] text-sm font-semibold text-white"
                style={{ background: '#00553E' }}
              >
                <Pencil size={14} />
                Fiche patient
              </NavLink>
            </div>
            <div className="mt-3 flex flex-wrap gap-3 items-center">
              {consultationStatus && (
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                    consultationStatus === "ouverte"
                      ? "bg-amber-100 text-amber-900"
                      : consultationStatus === "fermee"
                        ? "bg-[#DCEFE9] text-[#00553E]"
                        : "bg-red-100 text-red-700"
                  }`}
                >
                  Statut: {consultationStatus}
                </span>
              )}
            </div>
          </div>
        )}

        {verifiedPatient && monEtablissementsLoading && (
          <div className="mt-4 text-sm text-[#0B1F16]/60">Chargement de vos établissements…</div>
        )}

        {verifiedPatient && !monEtablissementsLoading && monEtablissements.length === 0 && (
          <div className="mt-4 rounded-[8px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Vous n'avez ni cabinet propre ni liaison active avec un établissement. Rendez-vous dans{" "}
            <NavLink to="/pro/dashboard/profil" className="font-semibold underline">
              votre profil
            </NavLink>{" "}
            pour créer un cabinet ou déclarer un établissement avant de créer une consultation.
          </div>
        )}

        {verifiedPatient && !monEtablissementsLoading && monEtablissements.length > 0 && (
          <div className="mt-4 space-y-1">
            <label className="block text-sm font-medium text-[#0B1F16]/80">
              Établissement (ou cabinet) sous lequel créer la consultation
            </label>
            <select
              value={selectedEtablissementId}
              onChange={(e) => setSelectedEtablissementId(e.target.value)}
              className="w-full h-10 rounded-[8px] border bg-white px-3"
              style={{ borderColor: "rgba(0,0,0,0.15)" }}
            >
              <option value="">Sélectionner…</option>
              {monEtablissements.map((item) => (
                <option key={item.liaison.id} value={item.etablissement.id}>
                  {item.etablissement.nom}
                  {item.liaison.source === "cabinet_auto" ? " (mon cabinet)" : ""}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {notFound && showCreatePatient && (
        <div
          className="mt-6 rounded-[10px] p-6 text-[#0B1F16]"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold text-lg">Créer le patient</div>
            <button
              type="button"
              onClick={() => setShowCreatePatient(false)}
              className="text-sm font-medium text-[#0B1F16]/70 hover:underline"
            >
              Annuler
            </button>
          </div>
          <PatientCreationWizard initialNip={nip} onCreated={handlePatientCreated} />
        </div>
      )}

      {verifiedPatient && (
        <div
          className="mt-6 rounded-[10px] p-6 text-[#0B1F16]"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <div className="font-semibold text-lg mb-3">Dossier médical</div>

          {dossierState === "loading" && (
            <p className="text-sm text-[#0B1F16]/60">Vérification de l'accès au dossier…</p>
          )}

          {dossierState === "error" && (
            <div className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {dossierError}
            </div>
          )}

          {dossierState === "needs-otp" && (
            <p className="text-sm text-amber-700">En attente d'autorisation d'accès du patient…</p>
          )}

          {dossierState === "filled" && (
            <div className="rounded-[8px] border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              Ce patient a déjà un dossier médical rempli
              {existingDossier?.numeroDossier ? ` (${existingDossier.numeroDossier})` : ""}.
            </div>
          )}

          {dossierState === "empty" && (
            <form onSubmit={handleCreateDossier} className="space-y-4">
              {dossierSubmitError && (
                <div className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  {dossierSubmitError}
                </div>
              )}
              <p className="text-sm text-[#0B1F16]/70">
                Ce patient a un compte mais aucun dossier médical n'a encore été rempli. Complétez-le maintenant pour
                pouvoir consulter ses informations médicales lors des prochaines visites.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#0B1F16]/70 mb-1">Taille (cm)</label>
                  <input
                    value={dossierTaille}
                    onChange={(e) => setDossierTaille(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    inputMode="numeric"
                    className="h-10 w-full rounded-[8px] border bg-white px-3"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#0B1F16]/70 mb-1">Groupe sanguin</label>
                  <select
                    value={dossierGs}
                    onChange={(e) => setDossierGs(e.target.value)}
                    className="h-10 w-full rounded-[8px] border bg-white px-3"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }}
                  >
                    {["", "O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"].map((o) => (
                      <option key={o} value={o}>{o || "Sélectionner"}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#0B1F16]/70 mb-1">Poids (kg)</label>
                  <input
                    value={dossierPoids}
                    onChange={(e) => setDossierPoids(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    inputMode="numeric"
                    className="h-10 w-full rounded-[8px] border bg-white px-3"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#0B1F16]/70 mb-1">Électrophorèse</label>
                  <select
                    value={dossierElectro}
                    onChange={(e) => setDossierElectro(e.target.value)}
                    className="h-10 w-full rounded-[8px] border bg-white px-3"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }}
                  >
                    {["", "AA", "AS", "SS", "AC", "SC"].map((o) => (
                      <option key={o} value={o}>{o || "Sélectionner"}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-[#0B1F16]/70 mb-1">Allergies</label>
                  <input
                    value={dossierAllergies}
                    onChange={(e) => setDossierAllergies(e.target.value)}
                    className="h-10 w-full rounded-[8px] border bg-white px-3"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }}
                    placeholder="Séparées par des virgules"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-[#0B1F16]/70 mb-2">Vaccinations</label>
                  <div className="flex flex-wrap gap-2">
                    {["Polio", "Fievre jaune", "COVID-19", "Varicelle", "BCG", "DTP", "MMR", "Hépatite", "HPV", "Hib"].map((opt) => {
                      const checked = dossierVaccins.some((v) => v.nom === opt);
                      return (
                        <label
                          key={opt}
                          className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-[8px] border text-xs cursor-pointer ${checked ? "border-[#00553E] bg-[#00553E]/10 text-[#00553E]" : "border-[rgba(0,0,0,0.15)] bg-white text-[#0B1F16]/70"}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              setDossierVaccins((prev) =>
                                e.target.checked
                                  ? [...prev, { nom: opt, lot: "", siteInjection: "" }]
                                  : prev.filter((v) => v.nom !== opt)
                              )
                            }
                            className="hidden"
                          />
                          {opt}
                        </label>
                      );
                    })}
                  </div>
                  {dossierVaccins.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {dossierVaccins.map((v) => (
                        <div key={v.nom} className="flex flex-wrap items-center gap-2 rounded-[8px] border bg-white p-2" style={{ borderColor: "rgba(0,0,0,0.15)" }}>
                          <span className="px-1 text-xs font-medium text-[#0B1F16]/70 w-24 shrink-0">{v.nom}</span>
                          <input
                            value={v.lot}
                            onChange={(e) =>
                              setDossierVaccins((prev) => prev.map((row) => (row.nom === v.nom ? { ...row, lot: e.target.value } : row)))
                            }
                            placeholder="N° Lot"
                            className="h-8 w-32 rounded-[8px] border bg-white px-2 text-xs"
                            style={{ borderColor: "rgba(0,0,0,0.15)" }}
                          />
                          <input
                            value={v.siteInjection}
                            onChange={(e) =>
                              setDossierVaccins((prev) => prev.map((row) => (row.nom === v.nom ? { ...row, siteInjection: e.target.value } : row)))
                            }
                            placeholder="Site injection"
                            className="h-8 w-36 rounded-[8px] border bg-white px-2 text-xs"
                            style={{ borderColor: "rgba(0,0,0,0.15)" }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={dossierSubmitting}
                  className="h-10 px-5 rounded-[6px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#00553E" }}
                >
                  {dossierSubmitting ? "Enregistrement…" : "Créer le dossier médical"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {verifiedPatient && (selectedEtablissementId || consultationId) && (
        <div
          className="mt-6 rounded-[10px] overflow-hidden text-[#0B1F16] flex flex-col"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(0,0,0,0.08)",
            maxHeight: "min(90vh, 720px)",
          }}
        >
          <div className="p-6 border-b shrink-0" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <h2 className="text-xl font-bold">Étape 2 — Consultation</h2>
            <p className="mt-1 text-sm text-[#0B1F16]/70">
              Ouverture: observations + analyses, puis clôture avec résultats + ordonnances + état de sortie.
            </p>
            <div className="mt-3 flex items-center gap-1 flex-wrap">
              {CONSULTATION_STEPS.map((step, idx) => (
                <span key={step.id} className="inline-flex items-center gap-1">
                  <span
                    className={`text-sm font-medium px-2 py-1 rounded ${
                      consultationStep === idx ? "bg-[#00553E] text-white" : "bg-black/5 text-[#0B1F16]/70"
                    }`}
                  >
                    {idx + 1}. {step.label}
                  </span>
                  {idx < CONSULTATION_STEPS.length - 1 && <ChevronRight size={16} className="text-[#0B1F16]/40" />}
                </span>
              ))}
            </div>
          </div>

          <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-5">
            {consultationStep === 0 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Observations</label>
                  <textarea
                    value={newConsultation.observations}
                    onChange={(e) => setNewConsultation((p) => ({ ...p, observations: e.target.value.toUpperCase() }))}
                    placeholder="SAISIR LES OBSERVATIONS DE LA CONSULTATION..."
                    className="w-full min-h-[140px] rounded-[8px] px-3 py-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40 resize-y"
                    style={{ borderColor: "rgba(0,0,0,0.15)", textTransform: "uppercase" }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Analyses effectuées ou demandées</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newConsultation.analysisInput}
                      onChange={(e) => setNewConsultation((p) => ({ ...p, analysisInput: e.target.value.toUpperCase() }))}
                      placeholder="EX. NFS, GLYCÉMIE..."
                      className="flex-1 h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                      style={{ borderColor: "rgba(0,0,0,0.15)", textTransform: "uppercase" }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const v = newConsultation.analysisInput.trim();
                          if (v) setNewConsultation((p) => ({ ...p, analyses: [...p.analyses, v], analysisInput: "" }));
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const v = newConsultation.analysisInput.trim();
                        if (v) setNewConsultation((p) => ({ ...p, analyses: [...p.analyses, v], analysisInput: "" }));
                      }}
                      className="h-10 px-4 rounded-[6px] font-semibold text-white shrink-0"
                      style={{ background: "#00553E" }}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                  {newConsultation.analyses.length > 0 && (
                    <ul className="space-y-1.5 rounded-[8px] p-3 bg-[#DAF1E9]/50">
                      {newConsultation.analyses.map((name, idx) => (
                        <li
                          key={idx}
                          className="flex w-full items-center justify-between gap-2 rounded-[6px] bg-white px-3 py-2"
                          style={{ border: "1px solid rgba(0,0,0,0.06)" }}
                        >
                          <span className="truncate text-sm text-[#0B1F16]">{name}</span>
                          <button
                            type="button"
                            onClick={() => setNewConsultation((p) => ({ ...p, analyses: p.analyses.filter((_, i) => i !== idx) }))}
                            className="text-red-500 hover:text-red-700 shrink-0"
                            aria-label="Retirer"
                          >
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Pièces jointes */}
                <div>
                  <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">
                    Pièces jointes
                  </label>
                  <div className="flex gap-2 items-center">
                    <label
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-[6px] border text-sm font-medium text-[#0B1F16] bg-white cursor-pointer hover:bg-[#F4FAF8]"
                      style={{ borderColor: "rgba(0,0,0,0.15)" }}
                    >
                      <Paperclip size={16} />
                      Choisir des fichiers
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const files = Array.from(e.target.files ?? []);
                          if (files.length) setAnalysisFiles((prev) => [...prev, ...files]);
                        }}
                      />
                    </label>
                    {analysisFiles.length > 0 && (
                      <button
                        type="button"
                        disabled={uploadingFiles}
                        onClick={() => void handleUploadAnalysisFiles()}
                        className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                        style={{ background: "#00553E" }}
                      >
                        {uploadingFiles ? "Envoi…" : `Envoyer (${analysisFiles.length})`}
                      </button>
                    )}
                  </div>

                  {/* Fichiers sélectionnés (en attente d'envoi) */}
                  {analysisFiles.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {analysisFiles.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#0B1F16]/70">
                          <Paperclip size={13} className="shrink-0" />
                          <span className="truncate">{f.name}</span>
                          <button
                            type="button"
                            onClick={() => setAnalysisFiles((prev) => prev.filter((_, idx) => idx !== i))}
                            className="ml-auto text-red-500 hover:text-red-700 shrink-0"
                          >
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Erreur d'upload */}
                  {uploadFileError && (
                    <p className="mt-2 text-sm text-red-600">{uploadFileError}</p>
                  )}

                  {/* Docs en attente (bucket OK, pas encore envoyés au backend — pas de consultationId) */}
                  {pendingDocs.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {pendingDocs.map((doc, i) => (
                        <li key={i}>
                          <FileAttachmentLink
                            file={{ url: doc.bucketUrl, fileName: doc.fileName, mimeType: doc.mimeType }}
                            trailing={
                              <div className="flex shrink-0 items-center gap-2">
                                <span className="text-xs font-medium text-amber-600">en attente</span>
                                <button
                                  type="button"
                                  onClick={() => setPendingDocs((prev) => prev.filter((_, idx) => idx !== i))}
                                  className="text-red-500 hover:text-red-700"
                                  aria-label="Retirer"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Docs envoyés au backend avec succès */}
                  {sentDocs.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-[#00553E] font-medium">
                        {sentDocs.length} fichier{sentDocs.length > 1 ? "s" : ""} joint{sentDocs.length > 1 ? "s" : ""} à la consultation.
                      </p>
                      <ul className="mt-2 space-y-2">
                        {sentDocs.map((doc, i) => (
                          <li key={i}>
                            <FileAttachmentLink
                              file={{ url: doc.bucketUrl, fileName: doc.fileName, mimeType: doc.mimeType }}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}
            {consultationStep === 1 && (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Diagnostic / Maladie</label>
                    <select
                      value={diseaseAutreMode ? "autre" : (newConsultation.disease === "Maladie" ? "" : newConsultation.disease)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "autre") {
                          setDiseaseAutreMode(true);
                          setNewConsultation((p) => ({ ...p, disease: "" }));
                        } else {
                          setDiseaseAutreMode(false);
                          setNewConsultation((p) => ({ ...p, disease: v }));
                        }
                      }}
                      disabled={loadingMaladies}
                      className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40 disabled:opacity-70"
                      style={{ borderColor: "rgba(0,0,0,0.15)" }}
                    >
                      <option value="">{loadingMaladies ? "Chargement…" : "Sélectionner une maladie"}</option>
                      {maladiesReferentiel.map((nom) => (
                        <option key={nom} value={nom}>{nom}</option>
                      ))}
                      <option value="autre">Autre</option>
                    </select>
                    {diseaseAutreMode && (
                      <input
                        type="text"
                        value={newConsultation.disease === "Maladie" ? "" : newConsultation.disease}
                        onChange={(e) => setNewConsultation((p) => ({ ...p, disease: e.target.value.toUpperCase() }))}
                        placeholder="EX. GRIPPE, ANGINE BACTÉRIENNE..."
                        className="mt-2 w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                        style={{ borderColor: "rgba(0,0,0,0.15)", textTransform: "uppercase" }}
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">État de sortie</label>
                    <select
                      value={etatSortie}
                      onChange={(e) => setEtatSortie(e.target.value as ConsultationExitState)}
                      className="w-full h-10 rounded-[6px] px-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                      style={{ borderColor: "rgba(0,0,0,0.15)" }}
                    >
                      {CONSULTATION_EXIT_STATES.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Résultats / Note</label>
                    <textarea
                      value={newConsultation.resultNote}
                      onChange={(e) => setNewConsultation((p) => ({ ...p, resultNote: e.target.value.toUpperCase() }))}
                      placeholder="COMMENTAIRE SUR LES RÉSULTATS..."
                      className="w-full min-h-[100px] rounded-[8px] px-3 py-3 border bg-white outline-none focus:ring-2 focus:ring-[#0CA37F]/40 resize-y"
                      style={{ borderColor: "rgba(0,0,0,0.15)", textTransform: "uppercase" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B1F16]/80 mb-2">Ordonnances</label>
                  <div className="grid grid-cols-[2fr_2fr_1fr] gap-4 mb-1 px-1">
                    <span className="text-xs font-semibold text-[#0B1F16]/60 uppercase tracking-wide">Médicament</span>
                    <span className="text-xs font-semibold text-[#0B1F16]/60 uppercase tracking-wide">Posologie</span>
                    <span className="text-xs font-semibold text-[#0B1F16]/60 uppercase tracking-wide">Renouvelable</span>
                  </div>
                  <div className="rounded-[8px] overflow-hidden border" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
                    {newConsultation.ordonnances.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-[2fr_2fr_1fr] border-b last:border-b-0 bg-[#DAF1E9]/30" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                        <input
                          value={row.medicament}
                          onChange={(e) => setNewConsultation((p) => ({ ...p, ordonnances: p.ordonnances.map((r, i) => i === idx ? { ...r, medicament: e.target.value.toUpperCase() } : r) }))}
                          className="p-3 bg-transparent outline-none font-medium border-r"
                          style={{ borderColor: "rgba(0,0,0,0.08)", textTransform: "uppercase" }}
                          placeholder="MÉDICAMENT"
                        />
                        <input
                          value={row.note}
                          onChange={(e) => setNewConsultation((p) => ({ ...p, ordonnances: p.ordonnances.map((r, i) => i === idx ? { ...r, note: e.target.value.toUpperCase() } : r) }))}
                          className="p-3 bg-transparent outline-none border-r"
                          style={{ borderColor: "rgba(0,0,0,0.08)", textTransform: "uppercase" }}
                          placeholder="POSOLOGIE"
                        />
                        <select
                          value={row.renouvelable ? "renouvelable" : "non_renouvelable"}
                          onChange={(e) => setNewConsultation((p) => ({ ...p, ordonnances: p.ordonnances.map((r, i) => i === idx ? { ...r, renouvelable: e.target.value === "renouvelable" } : r) }))}
                          className="p-3 bg-transparent outline-none text-sm"
                        >
                          <option value="non_renouvelable">Non renouvelable</option>
                          <option value="renouvelable">Renouvelable</option>
                        </select>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewConsultation((p) => ({ ...p, ordonnances: [...p.ordonnances, { medicament: "", note: "", renouvelable: false }] }))}
                    className="mt-3 inline-flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold"
                    style={{ background: "#00553E" }}
                    aria-label="Ajouter une ligne"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="p-6 border-t flex gap-2 justify-between shrink-0" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <div>
              {consultationStep > 0 ? (
                <button
                  type="button"
                  onClick={() => setConsultationStep((s) => s - 1)}
                  className="h-10 px-4 rounded-[6px] font-semibold border text-[#0B1F16] bg-white"
                  style={{ borderColor: "rgba(0,0,0,0.2)" }}
                >
                  Précédent
                </button>
              ) : null}
            </div>
            <div>
              {consultationStep === 0 ? (
                <button
                  type="button"
                  disabled={submitLoading}
                  onClick={() => {
                    // Consultation déjà ouverte (retour arrière) → on avance juste localement,
                    // sans re-soumettre (sinon handleSubmitConsultation la clôturerait).
                    if (consultationId) {
                      setConsultationStep(1);
                      return;
                    }
                    void handleSubmitConsultation();
                  }}
                  className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#00553E" }}
                >
                  {submitLoading ? "Ouverture…" : "Suivant →"}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={submitLoading}
                  onClick={() => void handleSubmitConsultation()}
                  className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#00553E" }}
                >
                  {submitLoading ? "Clôture…" : "Clôturer la consultation"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <SendOrdonnanceToPharmacyDialog
        open={pharmacyDialogOpen}
        onClose={() => setPharmacyDialogOpen(false)}
        ordonnanceOptions={pharmacySendOptions}
        onSent={() => showToast("Ordonnance envoyée à la pharmacie.", "success")}
      />

      <PatientAccessOtpModal
        open={dossierState === "needs-otp"}
        patientNip={verifiedPatient?.nip ?? ""}
        patientDisplayName={verifiedPatient ? [verifiedPatient.prenom, verifiedPatient.nom].filter(Boolean).join(" ") || undefined : undefined}
        onClose={() => setDossierState("idle")}
        onSuccess={() => {
          if (verifiedPatient) void loadDossier(verifiedPatient.nip);
        }}
      />
    </div>
  );
}
